// BM-H3851 (Vanessa, 7 Oct 2026). The Letter of Authority signing page.
//
// One link per signer: the client signs first, then the person they have
// authorised countersigns the acknowledgement. The token in the URL is the
// authorisation (backend/hub/loa_signing.py); there is no login.
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API } from "../config/api";
import { errorMessage } from "../utils/errorMessage";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import ClientSignatureField from "../components/ClientSignatureField";
import { signatureProblem, signaturePayload } from "../lib/clientSignature";
import { LOGO_URL } from "../config/constants";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";

export default function LoaSigningPage() {
  const { token } = useParams();
  const [letter, setLetter] = useState(null);
  const [loadError, setLoadError] = useState("");
  const [name, setName] = useState("");
  const [readConfirmed, setReadConfirmed] = useState(false);
  const [sigMode, setSigMode] = useState("draw");
  const [typedSignature, setTypedSignature] = useState("");
  const sigCanvasRef = useRef(null);
  const [errors, setErrors] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/loa/sign/${encodeURIComponent(token)}`)
      .then(res => { setLetter(res.data); setName(res.data.signer_name || ""); })
      .catch(e => setLoadError(errorMessage(e, "This signing link could not be opened.")));
  }, [token]);

  const submit = async () => {
    const missing = [];
    if (!name.trim()) missing.push("Your full name");
    if (!readConfirmed) missing.push("Confirm you have read the letter");
    const sigIssue = signatureProblem({
      mode: sigMode, typedName: typedSignature, fullName: name,
      canvasEmpty: !sigCanvasRef.current || sigCanvasRef.current.isEmpty(),
    });
    if (sigIssue) missing.push(sigIssue);
    setErrors(missing);
    if (missing.length) return;
    setSubmitting(true);
    try {
      await axios.post(`${API}/loa/sign/${encodeURIComponent(token)}`, {
        signer_name: name.trim(),
        signature: signaturePayload({ mode: sigMode, typedName: typedSignature, canvasRef: sigCanvasRef }),
        read_confirmed: true,
      });
      setDone(true);
    } catch (e) {
      setErrors([errorMessage(e, "Your signature could not be saved. Please try again.")]);
    } finally {
      setSubmitting(false);
    }
  };

  const shell = (children) => (
    <div className="min-h-screen bg-navy p-4" data-testid="loa-signing-page">
      <div className="max-w-3xl mx-auto space-y-4">
        <img src={LOGO_URL} alt="" className="h-10" />
        {children}
      </div>
    </div>
  );

  if (loadError) {
    return shell(
      <div className="p-4 rounded-md border border-status-red text-txt flex gap-2" data-testid="loa-load-error">
        <AlertCircle className="w-5 h-5 text-status-red" /> {loadError}
      </div>
    );
  }
  if (!letter) return shell(<Loader2 className="w-6 h-6 animate-spin text-txt-muted" />);

  if (done || letter.already_signed) {
    return shell(
      <div className="p-6 rounded-md border border-navy-border bg-navy-card text-txt space-y-2" data-testid="loa-signed">
        <CheckCircle2 className="w-6 h-6 text-status-green" />
        <p className="font-medium">Thank you, your signature has been recorded.</p>
        <p className="text-sm text-txt-muted">
          {letter.signer_role === "client"
            ? "We will now ask the person you have authorised to sign their acknowledgement. Once they have, we will email you both a signed and dated copy."
            : "We will email you and our client a signed and dated copy."}
        </p>
      </div>
    );
  }

  return shell(
    <>
      <div className="signature-paper rounded-md p-6 text-sm leading-relaxed" data-testid="loa-letter"
        dangerouslySetInnerHTML={{ __html: letter.letter_html }} />
      {letter.waiting_for_client ? (
        <div className="p-4 rounded-md border border-navy-border bg-navy-card text-txt text-sm" data-testid="loa-waiting-for-client">
          Our client has not signed this letter yet. We will email you again once they have.
        </div>
      ) : (
        <div className="p-4 rounded-md border border-navy-border bg-navy-card space-y-4">
          {letter.signer_role === "authorised_person" && (
            <p className="text-sm text-txt">
              By signing you confirm: I acknowledge this authority. I will give the Firm instructions only within its scope, and I will tell the Firm promptly if I stop being authorised to act.
            </p>
          )}
          <div className="space-y-1.5">
            <Label className="text-txt text-sm">Your full name</Label>
            <Input value={name} onChange={e => setName(e.target.value)} className="h-10" data-testid="loa-signer-name" />
          </div>
          <div className="flex items-start gap-2">
            <Checkbox id="loa-read" checked={readConfirmed} onCheckedChange={c => setReadConfirmed(!!c)} data-testid="loa-read-confirmed" />
            <label htmlFor="loa-read" className="text-sm text-txt-muted">I have read the whole Letter of Authority.</label>
          </div>
          <ClientSignatureField
            mode={sigMode} onModeChange={setSigMode}
            typedName={typedSignature} onTypedNameChange={setTypedSignature}
            canvasRef={sigCanvasRef} fullName={name} testIdPrefix="loa-"
          />
          {errors.length > 0 && (
            <ul className="text-sm text-status-red list-disc pl-5" data-testid="loa-errors">
              {errors.map(e => <li key={e}>{e}</li>)}
            </ul>
          )}
          <Button onClick={submit} disabled={submitting} className="w-full bg-teal hover:bg-teal-hover text-white" data-testid="loa-sign-submit">
            {submitting ? "Saving..." : "Sign the Letter of Authority"}
          </Button>
        </div>
      )}
    </>
  );
}
